import React, { useState, useEffect } from 'react';
import {
    AppBar as MuiAppBar, Toolbar, IconButton, Menu, MenuItem, Typography, Box,
    Avatar, Divider, ListItemIcon, alpha, Tooltip
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import Logo from '../../assets/Alerta Normativa-logo2026-claro.png';
import LogoBlanco from '../../assets/Alerta Normativa-logo2026-blanco.png';
import { jwtDecode } from 'jwt-decode';
import NotificacionesMenu from './NotificacionMenu';
import NotificacionBandejaMenu from './NotificacionBandejaMenu';
import ThemeToggle from './ThemeToggle';
import { useColorMode } from '../../styles/ColorModeProvider';

const AppBar = ({ handleDrawerToggle, isOpen, toggleDrawer }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [userName, setUserName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [rol, setRol] = useState('');
    const navigate = useNavigate();
    const { mode } = useColorMode();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserName(decoded["given_name"] || '');
                setUserLastName(decoded["family_name"] || '');
                setRol(decoded["rol"] || '');
                setIsAdmin(decoded["rol"] === "Administrador");
            } catch (error) {
                console.error('Error decoding token:', error);
                handleLogout();
            }
        } else {
            navigate('/login');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const isSuscriptor = rol === "Suscriptor";

    // Etiqueta legible del rol para el menú de perfil
    const rolLabel = isAdmin
        ? 'Administrador'
        : isSuscriptor
            ? 'Suscriptor'
            : 'Usuario';

    // El logo del suscriptor no debe llevar a /home (no es su vista);
    // lo mandamos a su bandeja.
    const homeHref = isSuscriptor ? "/suscriptor/bandeja" : "/home";

    const handleMenu = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const handlePerfilUsuario = () => {
        handleClose();
        navigate('/perfil-usuario');
    };

    const initials = `${userName[0] || ''}${userLastName[0] || ''}`.toUpperCase();

    return (
        <MuiAppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 60, sm: 68 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        aria-label="abrir menú"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <IconButton
                        aria-label="contraer menú"
                        edge="start"
                        onClick={toggleDrawer}
                        sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                        <Link style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }} to={homeHref}>
                            <Box
                                component="img"
                                src={mode === 'dark' ? LogoBlanco : Logo}
                                alt="Alerta Normativa Global"
                                sx={{ height: 44, display: 'block' }}
                            />
                        </Link>
                    </Box>
                    {isAdmin && (
                        <Tooltip title="Nueva normativa" arrow>
                            <IconButton
                                component={Link}
                                to="/nueva-normativa"
                                aria-label="Nueva normativa"
                                sx={{
                                    ml: 1,
                                    bgcolor: 'success.main',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'success.dark' },
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ThemeToggle />
                    {isAdmin && <NotificacionBandejaMenu />}
                    {/* Las notificaciones internas no aplican al suscriptor */}
                    {!isSuscriptor && <NotificacionesMenu />}

                    <Box
                        onClick={handleMenu}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            ml: 0.5,
                            pl: 1,
                            pr: { xs: 0.5, sm: 1 },
                            py: 0.5,
                            borderRadius: 999,
                            cursor: 'pointer',
                            transition: 'background-color .15s',
                            '&:hover': {
                                backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', lineHeight: 1.1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Bienvenido/a
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {userName}
                            </Typography>
                        </Box>
                        <Avatar
                            sx={{
                                width: 38, height: 38,
                                bgcolor: 'primary.main',
                                fontSize: '0.85rem',
                                fontWeight: 700,
                            }}
                        >
                            {initials}
                        </Avatar>
                    </Box>

                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        slotProps={{
                            paper: { sx: { mt: 1, minWidth: 200, borderRadius: 2 } },
                        }}
                    >
                        <Box sx={{ px: 2, py: 1 }}>
                            <Typography variant="subtitle2">{userName} {userLastName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {rolLabel}
                            </Typography>
                        </Box>
                        <Divider />
                        {/* El suscriptor no tiene vista de perfil interno */}
                        {!isSuscriptor && (
                            <MenuItem onClick={handlePerfilUsuario}>
                                <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
                                Perfil de usuario
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                            <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                            Cerrar sesión
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </MuiAppBar>
    );
};

export default AppBar;